/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Common Utility Functions
 *
 * Provides shared utilities for API response decryption and file uploads.
 */
import Swal from 'sweetalert2';
import { API_URL } from '../config';
import { decryptData } from './enode-decode';
import { devLog, devError } from '../../utils/devLogger';
import { store } from '../store';
import { getLoginObjDetails } from '../login/login.selector';

/** Maximum allowed file size for uploads (5 MB) */
const MAX_FILE_SIZE = 5 * 1024 * 1024;

/** Allowed MIME types for file uploads */
const ALLOWED_FILE_TYPES = [
	'image/jpeg',
	'image/png',
	'image/gif',
	'image/webp',
	'application/pdf',
];

/**
 * Decodes an encrypted hex response from the API.
 * The API returns encrypted data in a `response` field which is then
 * decrypted using the corresponding module-specific key.
 * @param response - The raw API response object containing an encrypted `response` field
 * @param key - The decryption key matching the encryption key used on the backend
 * @returns Decrypted and parsed JSON object, or null on failure
 */
export const decodeHexResponse = (response: any, key: string) => {
	if (!response || !response.response) {
		devError('decodeHexResponse: Invalid response structure:', response);
		return null;
	}

	const docodeVal = decryptData(response.response, key);
	// SECURITY FIX: Only log decrypted data in development
	devLog('decodeHexResponse result:', docodeVal);
	if (docodeVal === null) {
		devError('decodeHexResponse: Failed to decrypt response');
		return null;
	}
	return docodeVal;
};

/**
 * Validates a file before upload (type and size checks).
 * @param file - The File object to validate
 * @returns true if the file passes validation, false otherwise
 */
export const validateFile = (file: File): boolean => {
	// Check file size (max 5MB)
	if (file.size > MAX_FILE_SIZE) {
		Swal.fire({
			title: 'File Too Large',
			text: `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`,
			icon: 'warning',
			confirmButtonText: 'OK',
		});
		return false;
	}

	// Check file type
	if (!ALLOWED_FILE_TYPES.includes(file.type)) {
		Swal.fire({
			title: 'Invalid File Type',
			text: 'Only JPEG, PNG, GIF, WebP images and PDF files are allowed.',
			icon: 'warning',
			confirmButtonText: 'OK',
		});
		return false;
	}

	return true;
};

/**
 * Uploads an image/file to the server via multipart form data.
 * Used for employee photos, certificates, and organization logos.
 *
 * SECURITY FIX: Now includes JWT authentication header and client-side
 * file type/size validation before uploading.
 *
 * @param selectedFile - The File object to upload
 * @param employeeID - The employee ID to associate the file with (optional)
 * @param certificateType - The type of certificate/document being uploaded
 * @returns The file path on the server, or null on failure
 */
export const uploadImage = async (
	selectedFile: any,
	employeeID: any,
	certificateType: any,
): Promise<string | null> => {
	if (!selectedFile) return null;

	// SECURITY FIX: Validate file type and size before uploading
	if (selectedFile instanceof File && !validateFile(selectedFile)) {
		return null;
	}

	// SECURITY FIX: Retrieve JWT token for authenticated upload
	const state = store.getState();
	const token = getLoginObjDetails(state)?.jwtToken || null;

	const uploadFormData = new FormData();
	uploadFormData.append('file', selectedFile);
	uploadFormData.append('certificateType', certificateType);
	if (employeeID) {
		uploadFormData.append('employeeID', employeeID);
	}

	try {
		const response = await fetch(`${API_URL}upload.php`, {
			method: 'POST',
			body: uploadFormData,
			// SECURITY FIX: Include JWT Authorization header for authenticated uploads
			headers: {
				...(token ? { Authorization: `Bearer ${token}` } : {}),
			},
		});

		const responseText = await response.text();
		let result;

		try {
			result = JSON.parse(responseText);
		} catch (parseError) {
			// Fallback: check for 'success' string in non-JSON responses
			if (responseText.includes('success')) {
				result = { status: 'success' };
			} else {
				throw new Error('Invalid server response');
			}
		}

		if (result.status === 'success') {
			return result.filePath || null;
		}
		throw new Error(result.message || 'Logo upload failed');
	} catch (error: any) {
		devError('Logo upload error:', error);
		Swal.fire({
			title: 'Upload Error',
			text: error.message || 'Failed to upload logo',
			icon: 'error',
			confirmButtonText: 'OK',
		});
		return null;
	}
};
